import { Store } from "@tanstack/store";
import { API_URL } from "@/lib/constants";
import { COOKIE_KEYS, setCookie } from "@/lib/cookies";
import { api } from "@/services/api";
import { setTheme, type Theme } from "@/stores/theme-store";
import type { User } from "@/types/user";

const themeMapping: Record<number, Theme> = {
	0: "theme-gray",
	1: "theme-green",
	2: "theme-blue",
	3: "theme-purple",
	4: "theme-pink",
	5: "theme-red",
	6: "theme-orange",
	7: "theme-yellow",
};

interface AuthState {
	user: User | null;
	isAuthenticated: boolean;
	isLoading: boolean;
}

export const authStore = new Store<AuthState>({
	user: null,
	isAuthenticated: false,
	isLoading: true,
});

let authPromise: Promise<User> | null = null;

/**
 * Bootstrap da autenticação: busca o usuário corrente em /api/v3/user/.
 * A autenticação acontece via cookie HttpOnly `authorization` (convertido
 * em header pelo middleware do backend). Em 401, o response interceptor
 * de `api` tenta refresh automaticamente.
 */
export function initializeAuth(): Promise<User> {
	if (authPromise) return authPromise;

	authPromise = (async () => {
		authStore.setState((s) => ({ ...s, isLoading: true }));

		try {
			const { data: user } = await api.get<User>("/user/");

			if (user.colorModeTheme !== undefined) {
				const themeName = themeMapping[user.colorModeTheme];
				if (themeName) {
					setTheme(themeName);
				}
			}

			authStore.setState((s) => ({
				...s,
				user,
				isAuthenticated: true,
				isLoading: false,
			}));

			return user;
		} catch (error) {
			authStore.setState((s) => ({
				...s,
				user: null,
				isAuthenticated: false,
				isLoading: false,
			}));
			authPromise = null;
			throw error;
		}
	})();

	return authPromise;
}

/** Define que o usuário pulou o onboarding nesta sessão. */
export function setSkipOnboarding(): void {
	setCookie(COOKIE_KEYS.SKIP_ONBOARDING, "true", 1); // 1 dia de expiração
}

/** Navegação top-level para a tela de login do Django. Usada quando o
 *  refresh falha ou pela mutation de logout (após limpar o estado). */
export function redirectToLogin(): void {
	window.location.href = `${API_URL}/conta/entrar/`;
}
